import { z } from 'zod';
import { Decimal, allocateCents, dec } from '../money.js';
import { diffDays } from '../dates.js';
import { KeelError, assertNever } from '../errors.js';
import { AU_INDEXATION_CUTOFF } from '../tax/au.js';
import { jurisdictionFor } from '../tax/jurisdiction.js';
import { disposeAllocations, drpParcelId, isOpenParcel, parcelFromDrp, roundCents, unitsToString } from './parcels.js';
export const emptyPortfolioState = () => ({ parcels: [], cgtEvents: [], warnings: [] });
// ───────────── params schemas (see CorporateAction.params docs in types.ts) ─────────────
const pos = z.number().positive();
const id = z.string().min(1);
/** roundDown: floor resulting units to whole units (fractional entitlements usually rounded down / cashed out). */
const roundDown = z.boolean().prefault(false);
const RatioParams = z.object({ ratioFrom: pos, ratioTo: pos, roundDown });
const PerUnitParams = z.object({ perUnitCents: z.number().nonnegative() });
const ExchangeParams = z.object({ newSecurityId: id, ratioFor: pos, ratioHeld: pos, roundDown });
export const CORPORATE_ACTION_PARAM_SCHEMAS = {
    split: RatioParams,
    consolidation: RatioParams,
    bonus_issue: z.object({ ratioFor: pos, ratioHeld: pos, roundDown }),
    rights_issue: z.object({ ratioFor: pos, ratioHeld: pos, priceCents: z.number().nonnegative(), takenUp: z.boolean(), roundDown }),
    drp: z.object({ dividendId: id }),
    return_of_capital: PerUnitParams,
    demerger: z.object({
        newSecurityId: id, unitsRatioFor: pos, unitsRatioHeld: pos, costBasePctToNew: z.number().min(0).max(100),
        newAcquisitionDateRule: z.enum(['original', 'event']).prefault('original'), roundDown,
    }),
    takeover_cash: PerUnitParams,
    takeover_scrip: z.object({
        newSecurityId: id, ratioFor: pos, ratioHeld: pos, cashPerUnitCents: z.number().nonnegative().prefault(0),
        rollover: z.boolean().prefault(true),
        /** market value per NEW unit — required when there is a cash component or no rollover (to apportion / value the scrip). */
        scripValuePerNewUnitCents: z.number().nonnegative().optional(), roundDown,
    }),
    merger: ExchangeParams,
    unit_conversion: ExchangeParams,
    code_change: z.object({ newCode: z.string().min(1).optional(), newName: z.string().min(1).optional() }),
    name_change: z.object({ newCode: z.string().min(1).optional(), newName: z.string().min(1).optional() }),
    delisting: z.object({ disposeAtZero: z.boolean().prefault(false) }),
    amit_cost_base_adjustment: z.object({ netAmountPerUnitCents: z.number() }),
    special_dividend: z.object({}).passthrough(),
    capital_call: PerUnitParams,
};
const parseParams = (schema, action) => {
    const r = schema.safeParse(action.params);
    if (!r.success)
        throw new KeelError('VALIDATION', `Invalid params for ${action.type} (${action.id})`, r.error.issues);
    return r.data;
};
/** Validate an action's params against its type's schema; returns the parsed params. */
export const validateCorporateActionParams = (action) => parseParams(CORPORATE_ACTION_PARAM_SCHEMAS[action.type], action);
// ───────────── helpers ─────────────
const scale = (units, num, den, floor) => {
    const r = dec(units).mul(num).div(den);
    return floor ? r.floor() : dec(unitsToString(r));
};
const holdingKey = (p) => `${p.entityId}|${p.accountId}`;
/**
 * New units per entitled parcel for ratio num/den. With `floor` the rounding happens once per account holding and the
 * whole units are spread over that holding's parcels by largest remainder (earlier parcels win ties).
 */
const ratioUnits = (targets, num, den, floor) => {
    const out = new Map();
    if (!floor) {
        for (const p of targets)
            out.set(p.id, scale(p.remainingUnits, num, den, false));
        return out;
    }
    const groups = new Map();
    for (const p of targets)
        groups.set(holdingKey(p), [...(groups.get(holdingKey(p)) ?? []), p]);
    for (const ps of groups.values()) {
        const total = scale(ps.reduce((t, p) => t.plus(p.remainingUnits), dec(0)), num, den, true).toNumber();
        allocateCents(total, ps.map((p) => Number(p.remainingUnits))).forEach((u, i) => out.set(ps[i]?.id ?? '', dec(u)));
    }
    return out;
};
/** Parcel in the same holding with the most new units — absorbs the cost base of a sibling rounded to zero units. */
const absorberOf = (p, targets, units) => {
    let best;
    for (const q of targets) {
        const u = units.get(q.id) ?? dec(0);
        if (holdingKey(q) === holdingKey(p) && u.gt(0) && (!best || u.gt(units.get(best.id) ?? 0)))
            best = q;
    }
    return best;
};
/** Parcels entitled to the action: same security, open, matching entity, acquired before the ex date (or on/before date). */
const isEntitled = (p, a) => p.securityId === a.securityId && isOpenParcel(p) && (a.entityId === null || p.entityId === a.entityId)
    && (a.exDate ? p.acquiredDate < a.exDate : p.acquiredDate <= a.date);
const withAdj = (p, a, adj, patch) => ({
    ...p, ...patch,
    adjustments: [...p.adjustments, {
            date: a.date, sourceType: 'corporate_action', sourceId: a.id, unitsDelta: '0', costBaseDeltaCents: 0, reducedCostBaseDeltaCents: 0,
            note: a.type, ...adj,
        }],
    updatedAt: a.updatedAt,
});
const closeParcel = (p, a, note) => withAdj(p, a, {
    unitsDelta: unitsToString(dec(p.remainingUnits).neg()), costBaseDeltaCents: -p.costBaseCents,
    reducedCostBaseDeltaCents: -p.reducedCostBaseCents, note,
}, { remainingUnits: '0', costBaseCents: 0, reducedCostBaseCents: 0, closedDate: a.date });
const newParcel = (a, src, patch, note) => ({
    entityId: src.entityId, accountId: src.accountId, originalTradeId: null, originalUnits: patch.remainingUnits,
    isPre1999: patch.acquiredDate === src.acquiredDate ? src.isPre1999 : patch.acquiredDate < AU_INDEXATION_CUTOFF,
    adjustments: [{ date: a.date, sourceType: 'corporate_action', sourceId: a.id, unitsDelta: patch.remainingUnits, costBaseDeltaCents: patch.costBaseCents, reducedCostBaseDeltaCents: patch.reducedCostBaseCents, note }],
    closedDate: null, createdAt: a.createdAt, updatedAt: a.updatedAt, ...patch,
});
const requireSecurity = (securities, securityId, a) => {
    if (securities.length > 0 && !securities.some((s) => s.id === securityId)) {
        throw new KeelError('VALIDATION', `${a.type} ${a.id}: unknown newSecurityId ${securityId}`);
    }
};
/**
 * Apply one corporate action to the portfolio state deterministically. New parcel ids derive from the action and source
 * parcel ids so re-applying from scratch is idempotent. Throws KeelError('VALIDATION') for bad params.
 */
export const applyCorporateAction = (action, state, securities, opts) => {
    const { ctx } = opts;
    const a = action;
    const j = jurisdictionFor(ctx.jurisdiction);
    const targets = state.parcels.filter((p) => isEntitled(p, a));
    const mapTargets = (fn) => ({ ...state, parcels: state.parcels.map((p) => (isEntitled(p, a) ? fn(p) : p)) });
    const cgt = (p, proceeds, costBase, rcb) => j.computeDisposal({ parcel: p, units: p.remainingUnits, costBaseCents: costBase, reducedCostBaseCents: rcb, tradeId: null, corporateActionId: a.id, timestamp: a.updatedAt }, proceeds, a.date, ctx);
    /** Cost-base reduction (ROC / negative AMIT): excess over cost base → immediate capital gain (CGT events G1 / E10). */
    const reduceCostBase = (sourceType) => (p, amount) => {
        const cbCut = Math.min(amount, p.costBaseCents);
        const rcbCut = Math.min(amount, p.reducedCostBaseCents);
        const ev = amount > p.costBaseCents ? cgt(p, amount, p.costBaseCents, p.reducedCostBaseCents) : null;
        return {
            p: withAdj(p, a, { sourceType, costBaseDeltaCents: -cbCut, reducedCostBaseDeltaCents: -rcbCut }, { costBaseCents: p.costBaseCents - cbCut, reducedCostBaseCents: p.reducedCostBaseCents - rcbCut }),
            ev,
        };
    };
    const applyReductions = (perUnitCents, sourceType) => {
        const fn = reduceCostBase(sourceType);
        const events = [];
        const next = mapTargets((p) => { const r = fn(p, roundCents(dec(p.remainingUnits).mul(perUnitCents))); if (r.ev)
            events.push(r.ev); return r.p; });
        return { ...next, cgtEvents: [...state.cgtEvents, ...events] };
    };
    const increaseCostBase = (perUnitCents, sourceType) => mapTargets((p) => {
        const amt = roundCents(dec(p.remainingUnits).mul(perUnitCents));
        return withAdj(p, a, { sourceType, costBaseDeltaCents: amt, reducedCostBaseDeltaCents: amt }, { costBaseCents: p.costBaseCents + amt, reducedCostBaseCents: p.reducedCostBaseCents + amt });
    });
    const disposeList = (base, list, proceedsFor) => {
        let parcels = base.parcels;
        const events = [];
        for (const p of list) {
            const r = disposeAllocations({
                parcels, allocations: [{ parcelId: p.id, units: p.remainingUnits }], netProceedsCents: proceedsFor(p), date: a.date,
                tradeId: null, corporateActionId: a.id, timestamp: a.updatedAt, ctx,
            });
            parcels = r.parcels.map((x) => (x.id === p.id ? withAdj(x, a, { unitsDelta: unitsToString(dec(p.remainingUnits).neg()), costBaseDeltaCents: -p.costBaseCents, reducedCostBaseDeltaCents: -p.reducedCostBaseCents }, {}) : x));
            events.push(...r.cgtEvents);
        }
        return { ...base, parcels, cgtEvents: [...base.cgtEvents, ...events] };
    };
    const disposeAll = (proceedsFor) => disposeList(state, targets, proceedsFor);
    const info = (base, message) => ({ ...base, warnings: [...base.warnings, { eventId: a.id, eventType: a.type, date: a.date, code: 'INFO', message }] });
    /** Units changed in place (split/consolidation). Zero-unit parcels hand their cost base to a sibling, or are disposed. */
    const rescale = (units, originalFor) => {
        const moved = new Map();
        const lost = [];
        for (const p of targets) {
            if (!(units.get(p.id) ?? dec(0)).isZero())
                continue;
            const to = absorberOf(p, targets, units);
            if (!to) {
                lost.push(p);
                continue;
            }
            const m = moved.get(to.id) ?? { cb: 0, rcb: 0, from: [] };
            moved.set(to.id, { cb: m.cb + p.costBaseCents, rcb: m.rcb + p.reducedCostBaseCents, from: [...m.from, p.id] });
        }
        const lostIds = new Set(lost.map((p) => p.id));
        const next = mapTargets((p) => {
            if (lostIds.has(p.id))
                return p;
            const rem = units.get(p.id) ?? dec(0);
            if (rem.isZero())
                return closeParcel(p, a, `${a.type}: rounded to zero, cost base moved to ${absorberOf(p, targets, units)?.id ?? ''}`);
            const m = moved.get(p.id);
            return withAdj(p, a, {
                unitsDelta: unitsToString(rem.minus(p.remainingUnits)), costBaseDeltaCents: m?.cb ?? 0, reducedCostBaseDeltaCents: m?.rcb ?? 0,
                note: m ? `${a.type}; absorbed cost base of ${m.from.join(', ')}` : a.type,
            }, {
                remainingUnits: unitsToString(rem), originalUnits: unitsToString(Decimal.max(rem, originalFor(p))),
                costBaseCents: p.costBaseCents + (m?.cb ?? 0), reducedCostBaseCents: p.reducedCostBaseCents + (m?.rcb ?? 0),
            });
        });
        return lost.length === 0 ? next : info(disposeList(next, lost, () => 0), `Entitlement rounded to zero for ${lost.map((p) => p.id).join(', ')}: disposed for nil proceeds; record any cash in lieu as a sale`);
    };
    /** Scrip exchange (takeover_scrip / merger / unit_conversion). Rollover keeps cost base & acquisition date. */
    const exchange = (prm) => {
        requireSecurity(securities, prm.newSecurityId, a);
        const rollover = prm.rollover ?? true;
        const cashPU = prm.cashPerUnitCents ?? 0;
        if ((cashPU > 0 || !rollover) && prm.scripValuePerNewUnitCents === undefined) {
            throw new KeelError('VALIDATION', `${a.type} ${a.id}: scripValuePerNewUnitCents is required with a cash component or without rollover`);
        }
        const created = [];
        const events = [];
        const allUnits = ratioUnits(targets, prm.ratioFor, prm.ratioHeld, prm.roundDown);
        const merged = new Map();
        const zeroed = [];
        const next = mapTargets((p) => {
            const units = allUnits.get(p.id) ?? dec(0);
            const cash = roundCents(dec(p.remainingUnits).mul(cashPU));
            if (units.isZero()) {
                // no replacement units: roll the cost base into a sibling's new parcel, else it's a disposal for the cash (if any)
                const to = rollover && cash === 0 ? absorberOf(p, targets, allUnits) : undefined;
                if (to) {
                    const m = merged.get(to.id) ?? { cb: 0, rcb: 0 };
                    merged.set(to.id, { cb: m.cb + p.costBaseCents, rcb: m.rcb + p.reducedCostBaseCents });
                    return closeParcel(p, a, `${a.type}: rounded to zero, cost base rolled into parcel:${a.id}:${to.id}`);
                }
                events.push(cgt(p, cash, p.costBaseCents, p.reducedCostBaseCents));
                zeroed.push(p.id);
                return closeParcel(p, a, `${a.type}: no replacement units`);
            }
            const scripValue = roundCents(units.mul(prm.scripValuePerNewUnitCents ?? 0));
            let cb = p.costBaseCents, rcb = p.reducedCostBaseCents, acquired = p.acquiredDate;
            if (!rollover) {
                events.push(cgt(p, cash + scripValue, p.costBaseCents, p.reducedCostBaseCents));
                cb = scripValue;
                rcb = scripValue;
                acquired = a.date;
            }
            else if (cash > 0) {
                const cashCb = roundCents(dec(p.costBaseCents).mul(cash).div(cash + scripValue));
                const cashRcb = roundCents(dec(p.reducedCostBaseCents).mul(cash).div(cash + scripValue));
                events.push(cgt(p, cash, cashCb, cashRcb));
                cb -= cashCb;
                rcb -= cashRcb;
            }
            created.push(newParcel(a, p, {
                id: `parcel:${a.id}:${p.id}`, securityId: prm.newSecurityId, acquiredDate: acquired,
                remainingUnits: unitsToString(units), costBaseCents: cb, reducedCostBaseCents: rcb,
            }, `${a.type} from ${p.id}${rollover ? ' (rollover)' : ''}`));
            return closeParcel(p, a, `${a.type} to ${prm.newSecurityId}`);
        });
        const withMerged = created.map((np) => {
            const src = [...merged].find(([id]) => np.id === `parcel:${a.id}:${id}`)?.[1];
            return src ? { ...np, costBaseCents: np.costBaseCents + src.cb, reducedCostBaseCents: np.reducedCostBaseCents + src.rcb } : np;
        });
        const out = { ...next, parcels: [...next.parcels, ...withMerged], cgtEvents: [...state.cgtEvents, ...events] };
        return zeroed.length === 0 ? out : info(out, `No replacement units for ${zeroed.join(', ')} after rounding: treated as a disposal`);
    };
    switch (a.type) {
        case 'split':
        case 'consolidation': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.split, a);
            return rescale(ratioUnits(targets, prm.ratioTo, prm.ratioFrom, prm.roundDown), (p) => scale(p.originalUnits, prm.ratioTo, prm.ratioFrom, false));
        }
        case 'bonus_issue': {
            // Simplification: bonus units join the original parcel (same acquisition date), cost base spread over all units.
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.bonus_issue, a);
            const bonusUnits = ratioUnits(targets, prm.ratioFor, prm.ratioHeld, prm.roundDown);
            return mapTargets((p) => {
                const bonus = bonusUnits.get(p.id) ?? dec(0);
                return withAdj(p, a, { unitsDelta: unitsToString(bonus) }, {
                    remainingUnits: unitsToString(bonus.plus(p.remainingUnits)), originalUnits: unitsToString(bonus.plus(p.originalUnits)),
                });
            });
        }
        case 'rights_issue': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.rights_issue, a);
            if (!prm.takenUp) {
                return { ...state, warnings: [...state.warnings, { eventId: a.id, eventType: a.type, date: a.date, code: 'INFO', message: 'Rights not taken up: no parcel created (sale of renounceable rights is not modelled)' }] };
            }
            const groups = new Map();
            for (const p of targets) {
                const k = `${p.entityId}|${p.accountId}`;
                const g = groups.get(k);
                groups.set(k, { src: g?.src ?? p, units: (g?.units ?? dec(0)).plus(p.remainingUnits) });
            }
            const created = [...groups.values()].flatMap(({ src, units }) => {
                const u = scale(units, prm.ratioFor, prm.ratioHeld, prm.roundDown);
                if (!u.gt(0))
                    return [];
                const cost = roundCents(u.mul(prm.priceCents));
                return [newParcel(a, src, {
                        id: `parcel:${a.id}:${src.entityId}:${src.accountId}`, securityId: a.securityId, acquiredDate: a.date,
                        remainingUnits: unitsToString(u), costBaseCents: cost, reducedCostBaseCents: cost,
                    }, 'rights exercised')];
            });
            return { ...state, parcels: [...state.parcels, ...created] };
        }
        case 'drp': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.drp, a);
            if (state.parcels.some((p) => p.id === drpParcelId(prm.dividendId)))
                return state;
            const ev = (opts.incomeEvents ?? []).find((e) => e.id === prm.dividendId);
            if (!ev)
                throw new KeelError('VALIDATION', `drp ${a.id}: income event ${prm.dividendId} not found`);
            if (ev.securityId !== a.securityId)
                throw new KeelError('VALIDATION', `drp ${a.id}: income event is for another security`);
            return { ...state, parcels: [...state.parcels, parcelFromDrp(ev, ctx.baseCurrency)] };
        }
        case 'return_of_capital':
            return applyReductions(parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.return_of_capital, a).perUnitCents, 'return_of_capital');
        case 'amit_cost_base_adjustment': {
            const net = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.amit_cost_base_adjustment, a).netAmountPerUnitCents;
            return net >= 0 ? increaseCostBase(net, 'amit') : applyReductions(-net, 'amit');
        }
        case 'capital_call':
            return increaseCostBase(parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.capital_call, a).perUnitCents, 'corporate_action');
        case 'demerger': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.demerger, a);
            requireSecurity(securities, prm.newSecurityId, a);
            const created = [];
            const newUnits = ratioUnits(targets, prm.unitsRatioFor, prm.unitsRatioHeld, prm.roundDown);
            const none = [];
            const next = mapTargets((p) => {
                const units = newUnits.get(p.id) ?? dec(0);
                if (!units.gt(0)) {
                    none.push(p.id);
                    return p;
                } // no demerged units → no cost base moves
                const cbNew = roundCents(dec(p.costBaseCents).mul(prm.costBasePctToNew).div(100));
                const rcbNew = roundCents(dec(p.reducedCostBaseCents).mul(prm.costBasePctToNew).div(100));
                created.push(newParcel(a, p, {
                    id: `parcel:${a.id}:${p.id}`, securityId: prm.newSecurityId,
                    acquiredDate: prm.newAcquisitionDateRule === 'original' ? p.acquiredDate : a.date,
                    remainingUnits: unitsToString(units), costBaseCents: cbNew, reducedCostBaseCents: rcbNew,
                }, `demerged from ${p.id}`));
                return withAdj(p, a, { costBaseDeltaCents: -cbNew, reducedCostBaseDeltaCents: -rcbNew, note: `demerger to ${prm.newSecurityId}` }, { costBaseCents: p.costBaseCents - cbNew, reducedCostBaseCents: p.reducedCostBaseCents - rcbNew });
            });
            const out = { ...next, parcels: [...next.parcels, ...created] };
            return none.length === 0 ? out : info(out, `No demerged units for ${none.join(', ')} after rounding: cost base unchanged`);
        }
        case 'takeover_cash': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.takeover_cash, a);
            return disposeAll((p) => roundCents(dec(p.remainingUnits).mul(prm.perUnitCents)));
        }
        case 'takeover_scrip':
            return exchange(parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.takeover_scrip, a));
        case 'merger':
        case 'unit_conversion':
            return exchange(parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.merger, a));
        case 'delisting': {
            const prm = parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.delisting, a);
            return prm.disposeAtZero ? disposeAll(() => 0) : state;
        }
        case 'code_change':
        case 'name_change':
            parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.code_change, a);
            return state; // security master change only; parcels keep their securityId
        case 'special_dividend':
            parseParams(CORPORATE_ACTION_PARAM_SCHEMAS.special_dividend, a);
            return state; // income is recorded as an IncomeEvent
        default:
            return assertNever(a.type, 'corporate action type');
    }
};
const SUGGEST_RATIOS = [2, 3, 4, 5, 10];
/**
 * Heuristic split/consolidation suggestions from day-on-day close ratios ≈ ×½, ×⅓ … ×⅒ (split) or ×2 … ×10
 * (consolidation), for securities the entity has traded, after its first trade.
 * ponytail: fixed ratio list and ±tolerance; a genuine 50 % crash on a penny stock will be suggested too — user confirms.
 */
export const suggestCorporateActions = (prices, trades, existing = [], tolerance = 0.05) => {
    // an action already recorded for the security within ±3 days (by date or ex date) explains the gap
    const known = (securityId, d) => existing.some((a) => a.securityId === securityId
        && [a.date, a.exDate].some((x) => x !== null && Math.abs(diffDays(x, d)) <= 3));
    const firstTrade = new Map();
    for (const t of trades) {
        const d = firstTrade.get(t.securityId);
        if (!d || t.date < d)
            firstTrade.set(t.securityId, t.date);
    }
    const bySec = new Map();
    for (const p of prices)
        if (firstTrade.has(p.securityId))
            bySec.set(p.securityId, [...(bySec.get(p.securityId) ?? []), p]);
    const out = [];
    for (const [securityId, list] of bySec) {
        const sorted = [...list].sort((x, y) => (x.date < y.date ? -1 : 1));
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1], cur = sorted[i];
            if (!prev || !cur || cur.closeCents <= 0 || prev.closeCents <= 0 || cur.date <= (firstTrade.get(securityId) ?? '') || known(securityId, cur.date))
                continue;
            const r = prev.closeCents / cur.closeCents;
            for (const n of SUGGEST_RATIOS) {
                const isSplit = Math.abs(r / n - 1) <= tolerance;
                const isConsol = Math.abs(r * n - 1) <= tolerance;
                if (!isSplit && !isConsol)
                    continue;
                out.push({
                    entityId: null, securityId, type: isSplit ? 'split' : 'consolidation', date: cur.date, exDate: cur.date,
                    params: isSplit ? { ratioFrom: 1, ratioTo: n } : { ratioFrom: n, ratioTo: 1 },
                    appliedAt: null, source: 'suggested',
                    notes: `Close moved ${(prev.closeCents / 100).toFixed(2)} → ${(cur.closeCents / 100).toFixed(2)} on ${cur.date}`,
                });
                break;
            }
        }
    }
    return out;
};
//# sourceMappingURL=corporateActions.js.map